/**
 * Admin-only Simulation preview. Local preview data never replaces member records
 * outside Simulation. The retired global flag is cleared without deleting data.
 */
import type { MoneyCheckin, MoneyHabits } from "@/lib/desk/foundations";
import type { DeskCounters, Seat } from "@/lib/desk/career";
import { isSimulationPracticePath } from "./scope";

const ON_KEY = "decorum-simulation-practice-on";
const DATA_KEY = "decorum-practice-v1";

try { localStorage.removeItem("decorum-practice-on"); } catch { /* Storage unavailable. */ }

export interface PracticeDecision { choice_id: string; quality: string; wisdom: number }
export interface PracticeData {
  checkins: MoneyCheckin[];
  habits: MoneyHabits | null;
  lessons: string[];
  seat: Seat;
  decisions: Record<string, PracticeDecision>;
  desk: Partial<DeskCounters>;
  dayOffset: number;
  academy: string[];
  homework?: string[];
}

const EMPTY: PracticeData = { checkins: [], habits: null, lessons: [], seat: "intern", decisions: {}, desk: {}, dayOffset: 0, academy: [] };

const listeners = new Set<() => void>();
export function subscribePractice(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
const emit = () => listeners.forEach((f) => f());

export function isPracticeOn(pathname = typeof window === "undefined" ? "" : window.location.pathname): boolean {
  try { return isSimulationPracticePath(pathname) && localStorage.getItem(ON_KEY) === "1"; } catch { return false; }
}
export function setPracticeOn(on: boolean) {
  if (on && isSimulationPracticePath(typeof window === "undefined" ? "" : window.location.pathname)) localStorage.setItem(ON_KEY, "1");
  else localStorage.removeItem(ON_KEY);
  emit();
}

export function getPractice(): PracticeData {
  try {
    const raw = localStorage.getItem(DATA_KEY);
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : { ...EMPTY };
  } catch { return { ...EMPTY }; }
}
export function updatePractice(fn: (d: PracticeData) => PracticeData | void) {
  const cur = getPractice();
  const next = fn(cur) ?? cur;
  localStorage.setItem(DATA_KEY, JSON.stringify(next));
  emit();
}
export function resetPractice() {
  localStorage.removeItem(DATA_KEY);
  emit();
}

/** "Today" — shifted forward by the practice clock while practice is on. */
export function practiceNow(): Date {
  if (!isPracticeOn()) return new Date();
  return new Date(Date.now() + getPractice().dayOffset * 864e5);
}
export function advanceDays(n: number) {
  updatePractice((d) => ({ ...d, dayOffset: d.dayOffset + n }));
}
